import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  OrganizationChannel,
  ConfiguredChannel,
  DistributionList,
  Template,
  RenderedTemplate,
  Communication,
  EverbridgeTemplate,
  MassCommsContact,
  MassCommsContactDataset,
  EverbridgeReplaceTokensRequest,
  CommunicationRecipientActivity,
  CommunicationActivity
} from './communications.model';
import { QueryParams, QueryResult } from 'src/app/modules/core/store/models/query.model';
import { HttpService } from 'src/app/modules/core/services/http.service';
import { GUID_EMPTY } from '@app/core/constants';
import { FilterGroup } from 'src/app/modules/core/store/models';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class CommunicationsApiService {
  constructor(private http: HttpService) {}

  //
  // Channels
  //
  public getOrganizationChannels(params: QueryParams): Observable<QueryResult<OrganizationChannel>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/organizationChannels?${queryString}`;

    return this.http.get<QueryResult<OrganizationChannel>>(url);
  }

  public saveConfiguredChannel(channel: ConfiguredChannel): Observable<ConfiguredChannel> {
    if (channel.uId === GUID_EMPTY) {
      const url = `${apiBaseUrl}/v1/notifications/configuredChannels`;
      return this.http.post(url, channel);
    } else {
      const url = `${apiBaseUrl}/v1/notifications/configuredChannels/${channel.uId}`;
      return this.http.put(url, channel);
    }
  }

  public getConfiguredChannels(params: QueryParams): Observable<QueryResult<ConfiguredChannel>> {
    if (!params) {
      params = {
        skip: 0,
        take: 10,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };
    }

    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/configuredchannels?${queryString}`;

    return this.http.get<QueryResult<ConfiguredChannel>>(url);
  }

  public deleteConfiguredChannel(channel: ConfiguredChannel): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}`;

    return this.http.delete(url);
  }

  public getSlackChannelsForConfiguredChannel(channel: ConfiguredChannel): Observable<any[]> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/slack/channels`;

    return this.http.get(url);
  }

  public disconnectChannelFromSlack(channel: ConfiguredChannel): Observable<ConfiguredChannel> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/slack/disconnect`;

    return this.http.post(url);
  }

  public createChannelAuthStateToken(channel: ConfiguredChannel): Observable<string> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/create-state-token`;

    return this.http.get(url, { responseType: 'text' });
  }

  //
  // Distribution Lists
  //
  public getDistributionLists(params: QueryParams): Observable<QueryResult<DistributionList>> {
    if (!params) {
      params = {
        skip: 0,
        take: 10,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };
    }

    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/distributionLists?${queryString}`;

    return this.http.get<QueryResult<DistributionList>>(url);
  }

  public saveDistributionList(distributionList: DistributionList): Observable<DistributionList> {
    if (distributionList.uId === GUID_EMPTY) {
      const url = `${apiBaseUrl}/v1/notifications/distributionLists`;
      return this.http.post(url, distributionList);
    } else {
      const url = `${apiBaseUrl}/v1/notifications/distributionLists/${distributionList.uId}`;
      return this.http.put(url, distributionList);
    }
  }

  public deleteDistributionList(distributionList: DistributionList): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/distributionLists/${distributionList.uId}`;

    return this.http.delete(url);
  }

  public getDocumentsWithFilter(datasetUId: string, queryParams: QueryParams, filterGroup: FilterGroup): Observable<QueryResult<Document>> {
    const queryString = this.http.queryStringFromObject({ includeCount: true });

    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query?${queryString}`;

    const req = {
      filterGroup: filterGroup,
      calcFields: null,
      fields: null,
      sort: queryParams.sort,
      where: queryParams.where,
      pagination: {
        skip: queryParams.skip,
        take: queryParams.take
      }
    };

    return this.http.post<QueryResult<Document>>(url, req);
  }

  public getDataset(datasetUId: string): Observable<Dataset> {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}?includeWorkflow=true`;

    return this.http.get<Dataset>(url);
  }

  //
  // Communication Templates
  //
  public getTemplates(params: QueryParams): Observable<QueryResult<Template>> {
    if (!params) {
      params = {
        skip: 0,
        take: 10,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };
    }

    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/communicationtemplates?${queryString}`;

    return this.http.get<QueryResult<Template>>(url);
  }

  public saveTemplate(template: Template): Observable<Template> {
    if (template.uId === GUID_EMPTY) {
      const url = `${apiBaseUrl}/v1/notifications/communicationtemplates`;
      return this.http.post(url, template);
    } else {
      const url = `${apiBaseUrl}/v1/notifications/communicationtemplates/${template.uId}`;
      return this.http.put(url, template);
    }
  }

  public copyTemplate(template: Template): Observable<Template> {
    const url = `${apiBaseUrl}/v1/notifications/communicationtemplates/copy`;
    return this.http.post(url, template);
  }

  public deleteTemplate(template: Template): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/communicationtemplates/${template.uId}`;

    return this.http.delete(url);
  }

  public previewTemplate(communication: Communication): Observable<RenderedTemplate> {
    const url = `${apiBaseUrl}/v1/notifications/communications/preview`;

    return this.http.post(url, communication);
  }

  public getCommunication(communicationUId: string): Observable<Communication> {
    const url = `${apiBaseUrl}/v1/notifications/communications/${communicationUId}`;

    return this.http.get<Communication>(url);
  }

  public sendCommunication(communication: Communication): Observable<RenderedTemplate> {
    const url = `${apiBaseUrl}/v1/notifications/communications/send`;

    return this.http.post(url, communication);
  }

  public getCommunicationActivityForDocument(documentUId: string): Observable<QueryResult<CommunicationActivity>> {
    const url = `${apiBaseUrl}/v1/notifications/communications/communicationactivity/${documentUId}`;

    return this.http.get<QueryResult<CommunicationActivity>>(url);
  }

  public getCommunicationRecipientActivity(communicationUId: string): Observable<QueryResult<CommunicationRecipientActivity>> {
    const url = `${apiBaseUrl}/v1/notifications/communications/${communicationUId}/recipient-activity`;

    return this.http.get<QueryResult<CommunicationRecipientActivity>>(url);
  }

  public getEverbridgeTemplatesForConfiguredChannel(channel: ConfiguredChannel): Observable<EverbridgeTemplate[]> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/everbridge/templates`;

    return this.http.get(url);
  }

  public replaceEverbridgeTokens(template: EverbridgeTemplate, document: Document): Observable<EverbridgeTemplate> {
    let request: EverbridgeReplaceTokensRequest = { templateDto: template, documentDto: document };

    const url = `${apiBaseUrl}/v1/notifications/everbridge/replace-tokens`;

    return this.http.post(url, request);
  }


  ///
  /// Contacts
  ///
  public getMassCommsContactsFromDatasets(
    contactDatasets: MassCommsContactDataset[],
    skip: number = 0,
    take: number = 10,
    nameContains: string = null)
    : Observable<QueryResult<MassCommsContact>> {

    const queryString = this.http.queryStringFromObject({ skip, take, nameContains });

    const url = `${apiBaseUrl}/v1/notifications/Contacts/from-dataset?${queryString}`;

    return this.http.post<QueryResult<MassCommsContact>>(url, contactDatasets);
  }
}
